import { Dialog as HeadlessDialog } from "@headlessui/react";
import { X } from "lucide-react";
import PropTypes from "prop-types";
import React, { useState } from "react";

import { toSlug } from "../../utils";

const CreateDocumentTypeModal = ({ isOpen, onClose, onSubmit, categories, templateId }) => {
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    description: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const docTypeId = toSlug(formData.name);
      const docData = {
        name: formData.name,
        category: formData.category,
        description: formData.description,
        visitTypes: [],
        required: ["date", "provider", "facility"],
      };

      await onSubmit(templateId, docTypeId, docData);
      onClose();
      setFormData({ name: "", category: "", description: "" });
    } catch (error) {
      console.error("Error creating document type:", error);
    }
  };

  return (
    <HeadlessDialog open={isOpen} onClose={onClose} className="relative z-modal">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <HeadlessDialog.Panel className="relative w-full max-w-md rounded-lg bg-card p-8">
          <div className="absolute right-4 top-4">
            <button
              onClick={onClose}
              className="text-text-disabled hover:text-muted-foreground"
              type="button"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <HeadlessDialog.Title className="mb-6 text-xl font-semibold">
            Create Document Type
          </HeadlessDialog.Title>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="mb-2 block text-sm font-medium text-muted-foreground">
                Document Type Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full rounded-md border-input shadow-sm focus-visible:shadow-focus"
                required
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-muted-foreground">
                Category
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full rounded-md border-input shadow-sm focus-visible:shadow-focus"
                required
              >
                <option value="">Select a category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-muted-foreground">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full rounded-md border-input shadow-sm focus-visible:shadow-focus"
                rows={4}
                required
              />
            </div>

            <div className="mt-6 flex justify-end space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-input bg-card px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-surface-subtle"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover"
              >
                Create
              </button>
            </div>
          </form>
        </HeadlessDialog.Panel>
      </div>
    </HeadlessDialog>
  );
};

CreateDocumentTypeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  categories: PropTypes.array.isRequired,
  templateId: PropTypes.string.isRequired,
};

export default CreateDocumentTypeModal;
